/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { Typography2 } from '@coursera/cds-core';
import { AnswerCorrectIcon, AnswerWrongIcon, CheckIcon, CloseIcon } from '@coursera/cds-icons';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import CoachSkipLink, {
  type Props as CoachSkipLinkProps,
} from 'bundles/ai-coach-platform/components/patterns/chat/common/CoachSkipLink';
import {
  type CoachSuggestion,
  ResolutionStatus,
  type SuggestionAction,
} from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import Disclosure from 'bundles/cds-labs/components/Disclosure/Disclosure';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  coachSuggestion: (status: ResolutionStatus, expanded = false) => css`
    display: block;

    .disclosure {
      background-color: var(--cds-color-neutral-background-primary-weak) !important;
      transition: background-color 0.3s;
      border-radius: 0 !important;

      ${expanded && `background-color: var(--cds-color-emphasis-primary-background-weak) !important;`}

      .disclosure-content {
        ${status !== ResolutionStatus.UNRESOLVED && `display: none;`}
        max-height: 200px;
        overflow: auto;
        color: var(--cds-color-grey-700);
      }

      .disclosure-header {
        border-radius: 0 !important;
        padding: var(--cds-spacing-200) !important;
      }

      .disclosure-content,
      .disclosure-footer {
        padding-left: var(--cds-spacing-200) !important;
      }
    }

    &.last {
      .disclosure,
      .disclosure-header {
        border-bottom-left-radius: var(--cds-border-radius-200) !important;
        border-bottom-right-radius: var(--cds-border-radius-200) !important;
      }
    }
  `,
  containerHeader: css`
    position: relative;
    background-color: var(--cds-color-neutral-background-primary-weak) !important;
    padding: var(--cds-spacing-200);
    border-top-right-radius: var(--cds-border-radius-200);
    border-top-left-radius: var(--cds-border-radius-200);
    margin-bottom: var(--cds-spacing-50);

    .suggestion-header-title {
      color: var(--cds-color-grey-700);
    }
  `,
  suggestionHeader: css`
    display: flex;
    flex: 1;
    justify-content: space-between;
  `,
  suggestionFooter: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  suggestionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  suggestionTitle: css`
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: var(--cds-color-grey-700);
  `,

  skipLink: css`
    position: absolute;
    right: 16px;
    top: 8px;
  `,
  resolveIcon: css`
    display: flex;
  `,
};

type Props = {
  /**
   * List of coach suggestions with their current state
   */
  suggestions: CoachSuggestion[];

  /**
   * Callback when one of the suggestion disclosures is toggled
   */
  onDisclosureToggle?: (id: CoachSuggestion['id'], expanded: boolean) => void;

  /**
   * Custom header renderer for each suggestion
   */
  customSuggestionHeader?: ({
    suggestion,
    isResolved,
  }: {
    suggestion: CoachSuggestion;
    isResolved: boolean;
  }) => React.ReactNode;

  /**
   * custom label to use for the "Accept" suggestion button. Make sure to pass translated string.
   * @default "All suggestions"
   */
  suggestionsHeaderLabel?: string;

  /**
   * custom label to use for the "Accept" suggestion button. Make sure to pass translated string.
   * @default "Accept"
   */
  acceptLabel?: string;
  /**
   * custom label to use for the "Dismiss" suggestion button. Make sure to pass translated string.
   * @default "Dismiss"
   */
  dismissLabel?: string;

  /**
   * Config for a skip link to directly navigate to a particular DOM element of interest.
   * This is useful if there are lots of suggestions and we need a shortcut for keyboard users to skip
   * across all the suggestions.
   *
   * e.g. skip to an "Accept all suggestions" button that might be in another part of the UI, not immediately
   * after the suggestions container.
   *
   * @param targetRef ref of element to move focus to.
   * @param label text label for the skip link.
   */
  skipLink?: CoachSkipLinkProps;

  className?: string;
};

export const CoachSuggestions = (props: Props): React.ReactElement => {
  const {
    suggestions,
    onDisclosureToggle,
    customSuggestionHeader,
    suggestionsHeaderLabel = _t('All suggestions'),
    acceptLabel = _t('Accept'),
    dismissLabel = _t('Dismiss'),
    skipLink,
    className,
  } = props;
  const isMounted = React.useRef<boolean>(false);
  const [expandedSuggestion, setExpandedSuggestion] = React.useState<string | null>(null);
  // a11y: maintain map of suggestion node to set keyboard focus on resolution
  const suggestionNodesMap = React.useRef<Record<string, HTMLDivElement>>({});
  const trackingData = useCoachTrackingContext()?.data;
  const track = useTracker();
  const visibilityRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_coach_suggestions', {
    suggestionsCount: suggestions.length,
    suggestionIds: suggestions.map((s) => s.id),
    coach: {
      mode: trackingData?.mode ?? 'UNKNOWN_UNDEFINED',
    },
  });

  const numResolved = suggestions.reduce(
    (count, { status }) => (status !== ResolutionStatus.UNRESOLVED ? count + 1 : count),
    0
  );

  const handleToggle = React.useCallback<(id: CoachSuggestion['id']) => void>(
    (id) => {
      // collapses current suggestion i.e. all suggestions collapsed
      const expandedSuggestionId = expandedSuggestion === id ? null : id;

      setExpandedSuggestion(expandedSuggestionId);

      onDisclosureToggle?.(id, !!expandedSuggestionId || false);
    },
    [expandedSuggestion, onDisclosureToggle]
  );

  React.useEffect(() => {
    if (isMounted.current === false) {
      isMounted.current = true;
      // expand first unresolved suggestion on mount
      const firstUnresolvedSuggestion = suggestions.find((s) => s.status === ResolutionStatus.UNRESOLVED);

      if (firstUnresolvedSuggestion) {
        handleToggle(firstUnresolvedSuggestion.id);
      }
    } else if (suggestions.filter((s) => s.status === ResolutionStatus.UNRESOLVED).length === 0) {
      // reset state once all suggestions are resolved, this can happen for the "accept/reject all" case
      setExpandedSuggestion(null);
    }
  }, [suggestions, handleToggle]);

  const handleExpandNextUnresolved = (id: CoachSuggestion['id']) => {
    // always finds in order regardless of position of current resolved suggestion, and skip current suggestion
    const unresolved = suggestions.filter((s) => s.status === ResolutionStatus.UNRESOLVED && s.id !== id);

    if (unresolved.length > 0) {
      const nextUnresolved = unresolved[0].id;

      handleToggle(nextUnresolved);

      // programmatically focus on the header of next unresolved
      const header = suggestionNodesMap.current[nextUnresolved]?.querySelector('[role="button"]') as HTMLDivElement;
      header?.focus();
    }
  };

  const handleResolve = (suggestion: CoachSuggestion, action: SuggestionAction) => {
    const { onAction, id } = suggestion;
    handleToggle(id);
    onAction(action);

    // auto-expand the next unresolved suggestion
    handleExpandNextUnresolved(id);

    if (trackingData) {
      track('interact_coach_suggestions', {
        interactionType: action,
        suggestionIds: [id],
        coach: {
          mode: trackingData.mode,
        },
      });
    }
  };

  const renderResolveIcon = (status: ResolutionStatus) => {
    let icon;

    switch (status) {
      case ResolutionStatus.ACCEPTED:
        icon = <AnswerCorrectIcon color="success" />;
        break;

      case ResolutionStatus.REJECTED:
        icon = <AnswerWrongIcon color="error" />;
        break;

      default:
        icon = null;
        break;
    }

    return <div css={styles.resolveIcon}>{icon}</div>;
  };

  const renderHeader = ({
    suggestion,
    isResolved,
  }: {
    suggestion: CoachSuggestion;
    isResolved: boolean;
  }): React.ReactNode => {
    if (typeof customSuggestionHeader === 'function') {
      return customSuggestionHeader({ suggestion, isResolved });
    }

    return (
      <div css={styles.suggestionHeader}>
        <Typography2 variant="bodySecondary" component="div" css={styles.suggestionTitle}>
          {suggestion.title}
        </Typography2>
        {isResolved && renderResolveIcon(suggestion.status)}
      </div>
    );
  };

  const renderFooter = ({ suggestion }: { suggestion: CoachSuggestion }) => {
    const handleClick = (action: SuggestionAction) => {
      handleResolve(suggestion, action);
    };

    return (
      <div css={styles.suggestionFooter}>
        <CoachButton
          onClick={() => handleClick('accept')}
          variant="secondary"
          icon={<CheckIcon />}
          iconPosition="before"
          size="small"
          aria-label={_t('Accept suggestion')}
        >
          {acceptLabel}
        </CoachButton>
        <CoachButton
          onClick={() => handleClick('reject')}
          variant="ghost"
          icon={<CloseIcon />}
          iconPosition="before"
          size="small"
          aria-label={_t('Dismiss suggestion')}
        >
          {dismissLabel}
        </CoachButton>
      </div>
    );
  };

  return (
    <div
      className={classNames('coach-chat-suggestions', className)}
      role="group"
      aria-label={_t('All suggestions, #{numResolved} of #{total} resolved', {
        numResolved,
        total: suggestions.length,
      })}
      ref={visibilityRef}
    >
      <div css={styles.containerHeader}>
        <Typography2 variant="actionSecondary" component="h3" className="suggestion-header-title">
          {suggestionsHeaderLabel}
        </Typography2>
        {skipLink ? <CoachSkipLink {...skipLink} visibleCss={styles.skipLink} /> : null}
      </div>
      <div className="suggestions-container" css={styles.suggestionsContainer}>
        {suggestions.map((suggestion, index) => {
          const isResolved = suggestion.status !== ResolutionStatus.UNRESOLVED;
          const isLast = index === suggestions.length - 1;
          const isExpanded = suggestion.id === expandedSuggestion;
          const suggestionHeader = renderHeader({ suggestion, isResolved });
          const ariaLabel =
            suggestion.ariaLabel ?? (typeof suggestion.title === 'string' ? suggestion.title : _t('Suggestion'));

          return (
            <div
              css={styles.coachSuggestion(suggestion.status, isExpanded)}
              className={classNames('coach-suggestion', { last: isLast })}
              key={suggestion.id}
              aria-disabled={isResolved}
            >
              <Disclosure
                collapsible={!isResolved} // collapsed and read-only once resolved
                expanded={isExpanded} // allows us to auto-toggle each disclosure
                defaultExpanded={false} // all suggestions collapsed by default, but controlled by `isExpanded`
                onToggle={() => handleToggle(suggestion.id)}
                header={suggestionHeader}
                aria-label={`${ariaLabel}, ${suggestion.status}`}
                footer={isResolved ? null : renderFooter({ suggestion })}
                ref={(node) => {
                  suggestionNodesMap.current[suggestion.id] = node as HTMLDivElement;
                }}
              >
                {isResolved ? null : suggestion.content}
              </Disclosure>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CoachSuggestions;
